import { useMemo } from 'react'
import useApiService from 'services/api'
import { getTargetType } from './targetTypeMap'
import {
  adaptUnifiedItemForVertical,
  adaptUnifiedListParams,
  VerticalRowConfig,
} from './unifiedResponseAdapter'

/**
 * Unified data-source actions for an opportunities target table (FIR-6995,
 * default-on since the FIR-6997 cutover).
 *
 * Returns `{ listAction, summaryAction }` bound to a vertical. The actions hit
 * the unified `/opportunities/` endpoint (resolving the vertical through
 * `getTargetType`, shaping outgoing params via `adaptUnifiedListParams`, and
 * re-nesting each row via `adaptUnifiedItemForVertical` so the per-vertical
 * columns/links/actions resolve).
 *
 * `DECommonTable` calls these as `(params)` (no target_type, no signal) — the
 * target_type is bound here and the adapter handles param/row shaping, so the
 * generic table needs no knowledge of the unified contract.
 *
 * @param vertical app vertical / sub-category slug (routed through `getTargetType`)
 * @param rowConfig how to re-nest the flat unified row for this vertical
 */
export function useUnifiedTableActions<T extends { id: number }>(
  vertical: string,
  rowConfig: VerticalRowConfig
): {
  listAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ results: T[] }>
  summaryAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ count: number; alt_count: number }>
} {
  const api = useApiService()

  // Stable scalar key for the array-valued `companyIdAliases` dep below.
  const companyIdAliasesKey = rowConfig.companyIdAliases?.join(',') ?? ''

  return useMemo(() => {
    const targetType = getTargetType(vertical)

    const listAction = async (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => {
      const res = await api.getOpportunitiesList(
        targetType,
        adaptUnifiedListParams(params, targetType),
        signal
      )
      return {
        ...res,
        results: res.results.map((item) =>
          adaptUnifiedItemForVertical(
            item as Record<string, unknown>,
            rowConfig
          )
        ),
      } as unknown as { results: T[] }
    }

    const summaryAction = async (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => {
      const res = await api.getOpportunitiesSummary(
        targetType,
        adaptUnifiedListParams(params, targetType),
        signal
      )
      // The opps (chains) summary carries the chain count in `count` and the
      // summed-door count in `places_count`; it never emits `alt_count` (only the
      // doors summary does, for its distinct-chain count). The shared stat plumbing
      // reads the secondary count from `alt_count`, so map `places_count` into it —
      // that's the lodging/cstore "Total Doors" card. Falling back through
      // `res.alt_count` first keeps any future alt_count-emitting summary working.
      // Verticals that only surface `count` ignore the extra value. (FIR-8027)
      return {
        count: res.count,
        alt_count: res.alt_count ?? res.places_count ?? 0,
      }
    }

    return { listAction, summaryAction }
    // Keyed on the vertical + row config; `rowConfig` object identity is not
    // stable at call sites, so depend on its scalar fields instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vertical,
    api,
    rowConfig.nestKey,
    rowConfig.detailLinkIdField,
    companyIdAliasesKey,
  ])
}
